/**
 * Pinia Auth Store
 * OP-2026-0129: Frontend Admin SaaS
 *
 * IMPORTANTE (L028): Refs Vue DEVEM ser module-level para estado compartilhado
 * IMPORTANTE (L020): Mock de auth DEVE consultar backend (não inventar dados)
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { initializeApp } from 'firebase/app'
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth'
import { api } from '@/api/client'

// Firebase config (será preenchido em produção)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID
}

// Inicializar Firebase apenas se configurado
let firebaseApp = null
let auth = null

if (firebaseConfig.apiKey) {
  firebaseApp = initializeApp(firebaseConfig)
  auth = getAuth(firebaseApp)
}

export const useAuthStore = defineStore('auth', () => {
  // ===== STATE (module-level refs - L028) =====
  const user = ref(null)
  const token = ref(null)
  const tenantId = ref(null)
  const loading = ref(true)
  const error = ref(null)

  // ===== GETTERS =====
  const isAuthenticated = computed(() => !!token.value)
  const userEmail = computed(() => user.value?.email || '')
  const userName = computed(() => user.value?.name || user.value?.email || 'Usuário')
  const userRole = computed(() => user.value?.role || 'user')

  // ===== ACTIONS =====

  /**
   * Login com email/senha
   */
  async function login(email, password) {
    loading.value = true
    error.value = null

    try {
      // Mock auth para desenvolvimento (L020: consulta backend)
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        return await mockLogin(email)
      }

      // Firebase auth
      if (!auth) {
        throw new Error('Firebase não configurado')
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password)
      const idToken = await userCredential.user.getIdToken()

      // Validar usuário no backend (L020)
      await validateUserWithBackend(idToken, userCredential.user.email)

      return true
    } catch (err) {
      error.value = parseFirebaseError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Login com Google
   */
  async function loginWithGoogle() {
    loading.value = true
    error.value = null

    try {
      if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
        return await mockLogin(import.meta.env.VITE_MOCK_USER_EMAIL)
      }

      if (!auth) {
        throw new Error('Firebase não configurado')
      }

      const provider = new GoogleAuthProvider()
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken()

      // Validar usuário no backend (L020)
      await validateUserWithBackend(idToken, userCredential.user.email)

      return true
    } catch (err) {
      error.value = parseFirebaseError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Mock login para desenvolvimento
   * L020: DEVE consultar backend para validar usuário
   */
  async function mockLogin(email) {
    try {
      // Consultar backend para validar usuário (L020)
      const response = await api.post('/api/auth/validate-user', { email })

      if (response.data.authorized) {
        user.value = {
          email: response.data.user.email,
          name: response.data.user.nome,
          role: response.data.user.role,
          tenantId: response.data.user.tenant_id
        }
        token.value = 'mock-token-' + Date.now()
        tenantId.value = response.data.user.tenant_id

        // Persistir
        localStorage.setItem('auth_token', token.value)
        localStorage.setItem('tenant_id', tenantId.value)
        localStorage.setItem('user', JSON.stringify(user.value))

        return true
      } else {
        error.value = 'Usuário não encontrado ou inativo'
        return false
      }
    } catch (err) {
      // Se backend não responder em dev, usar fallback
      if (import.meta.env.VITE_DEBUG === 'true') {
        console.warn('[Auth] Backend não disponível, usando fallback dev')
        user.value = {
          email,
          name: 'Dev User',
          role: 'admin',
          tenantId: 1
        }
        token.value = 'mock-token-' + Date.now()
        tenantId.value = 1

        localStorage.setItem('auth_token', token.value)
        localStorage.setItem('tenant_id', String(tenantId.value))
        localStorage.setItem('user', JSON.stringify(user.value))

        return true
      }
      throw err
    }
  }

  /**
   * Validar usuário no backend após login Firebase
   * L020: Não inventar dados - consultar backend
   */
  async function validateUserWithBackend(idToken, email) {
    const response = await api.post('/api/auth/validate-user', { email })

    if (!response.data.authorized) {
      throw new Error('Usuário não autorizado')
    }

    user.value = {
      email: response.data.user.email,
      name: response.data.user.nome,
      role: response.data.user.role,
      tenantId: response.data.user.tenant_id
    }
    token.value = idToken
    tenantId.value = response.data.user.tenant_id

    // Persistir
    localStorage.setItem('auth_token', token.value)
    localStorage.setItem('tenant_id', String(tenantId.value))
    localStorage.setItem('user', JSON.stringify(user.value))
  }

  /**
   * Logout
   */
  async function logout() {
    try {
      if (auth && import.meta.env.VITE_USE_MOCK_AUTH !== 'true') {
        await firebaseSignOut(auth)
      }
    } catch (err) {
      console.error('[Auth] Erro no logout Firebase:', err)
    }

    // Limpar estado
    user.value = null
    token.value = null
    tenantId.value = null
    error.value = null

    // Limpar localStorage
    localStorage.removeItem('auth_token')
    localStorage.removeItem('tenant_id')
    localStorage.removeItem('user')
  }

  /**
   * Restaurar sessão do localStorage
   */
  function restoreSession() {
    const storedToken = localStorage.getItem('auth_token')
    const storedTenantId = localStorage.getItem('tenant_id')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      token.value = storedToken
      tenantId.value = storedTenantId ? parseInt(storedTenantId, 10) : null
      try {
        user.value = JSON.parse(storedUser)
      } catch {
        user.value = null
      }
    }

    loading.value = false
  }

  /**
   * Inicializar listener de auth state (Firebase)
   */
  function initAuthListener() {
    if (auth && import.meta.env.VITE_USE_MOCK_AUTH !== 'true') {
      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          const idToken = await firebaseUser.getIdToken()
          token.value = idToken
          localStorage.setItem('auth_token', idToken)
        } else {
          // Usuário deslogou
          if (!import.meta.env.VITE_USE_MOCK_AUTH) {
            await logout()
          }
        }
        loading.value = false
      })
    } else {
      // Mock auth - restaurar sessão
      restoreSession()
    }
  }

  /**
   * Parser de erros Firebase
   */
  function parseFirebaseError(err) {
    const errorMap = {
      'auth/invalid-email': 'Email inválido',
      'auth/user-disabled': 'Usuário desabilitado',
      'auth/user-not-found': 'Usuário não encontrado',
      'auth/wrong-password': 'Senha incorreta',
      'auth/email-already-in-use': 'Email já em uso',
      'auth/weak-password': 'Senha muito fraca',
      'auth/network-request-failed': 'Erro de conexão',
      'auth/popup-closed-by-user': 'Login cancelado',
      'auth/cancelled-popup-request': 'Login cancelado'
    }

    return errorMap[err.code] || err.message || 'Erro desconhecido'
  }

  // ===== RETURN =====
  return {
    // State
    user,
    token,
    tenantId,
    loading,
    error,

    // Getters
    isAuthenticated,
    userEmail,
    userName,
    userRole,

    // Actions
    login,
    loginWithGoogle,
    logout,
    restoreSession,
    initAuthListener
  }
})
